// Reusable form widgets — time picker, number stepper, segmented control.
//
// Each builder returns a small `{ el, getValue, setValue, onChange }` object
// rather than a raw input, so callers can wire change listeners without
// pulling DOM event handling into their own code. The pickers expose their
// own getValue/setValue so the parent flow can read or restore values
// (e.g. when applying a saved template to the new-event sheet).
//
// Pure DOM — no state, no api, no router.

import { h, icon } from "./dom";

export interface TimePickerControl {
  el: HTMLElement;
  getValue: () => string;
  setValue: (hhmm: string) => void;
  onChange: (fn: () => void) => void;
}

export interface MiniTimePickerControl {
  el: HTMLElement;
  getValue: () => string;
  setValue: (hhmm: string) => void;
}

export interface NumberStepperControl {
  el: HTMLElement;
  getValue: () => number;
  setValue: (next: number) => void;
  onChange: (fn: (val: number) => void) => void;
  onUserChange: (fn: (val: number) => void) => void;
}

// Reusable time picker: getValue returns "HH:MM"
export function buildTimePicker(initialHH = "10", initialMM = "00"): TimePickerControl {
  const hh = h("select", { class: "input input--select" }) as HTMLSelectElement;
  for (let i = 0; i < 24; i++) {
    const v = String(i).padStart(2, "0");
    const opt = h("option", { value: v }, `${v}시`) as HTMLOptionElement;
    if (v === initialHH) opt.selected = true;
    hh.append(opt);
  }
  const mm = h("select", { class: "input input--select" }) as HTMLSelectElement;
  for (const m of ["00", "10", "20", "30", "40", "50"]) {
    const opt = h("option", { value: m }, `${m}분`) as HTMLOptionElement;
    if (m === initialMM) opt.selected = true;
    mm.append(opt);
  }
  const listeners: Array<() => void> = [];
  const fire = () => listeners.forEach((fn) => fn());
  hh.onchange = fire;
  mm.onchange = fire;
  const el = h("div", { class: "time-picker" }, [hh, mm]);
  return {
    el,
    getValue: () => `${hh.value}:${mm.value}`,
    setValue: (hhmm: string) => {
      if (!hhmm || typeof hhmm !== "string") return;
      const [newHH, newMMraw] = hhmm.split(":");
      if (!newHH || newMMraw === undefined) return;
      // Snap MM to nearest 10-minute step
      const mNum = Math.max(0, Math.min(50, Math.round(Number(newMMraw) / 10) * 10));
      const newMM = String(mNum).padStart(2, "0");
      if (Array.from(hh.options).some((o) => o.value === newHH)) hh.value = newHH;
      if (Array.from(mm.options).some((o) => o.value === newMM)) mm.value = newMM;
      fire();
    },
    onChange: (fn) => {
      listeners.push(fn);
    },
  };
}

export function buildMiniTimePicker(
  initialHHMM: string | null | undefined,
  onChange: (hhmm: string) => void,
): MiniTimePickerControl {
  const [iHH, iMM] = (initialHHMM || "00:00").split(":");
  const hh = h("select", { class: "input input--select input--mini" }) as HTMLSelectElement;
  for (let i = 0; i < 24; i++) {
    const v = String(i).padStart(2, "0");
    const opt = h("option", { value: v }, `${v}시`) as HTMLOptionElement;
    if (v === (iHH || "00")) opt.selected = true;
    hh.append(opt);
  }
  const mm = h("select", { class: "input input--select input--mini" }) as HTMLSelectElement;
  for (const m of ["00", "10", "20", "30", "40", "50"]) {
    const opt = h("option", { value: m }, `${m}분`) as HTMLOptionElement;
    if (m === (iMM || "00")) opt.selected = true;
    mm.append(opt);
  }
  const fire = () => {
    if (onChange) onChange(`${hh.value}:${mm.value}`);
  };
  hh.onchange = fire;
  mm.onchange = fire;
  const el = h("div", { class: "time-picker time-picker--mini" }, [hh, mm]);
  return {
    el,
    getValue: () => `${hh.value}:${mm.value}`,
    setValue: (hhmm: string) => {
      if (!hhmm) return;
      const [newHH, rawMM] = hhmm.split(":");
      const mNum = Math.max(0, Math.min(50, Math.round(Number(rawMM) / 10) * 10));
      const newMM = String(mNum).padStart(2, "0");
      if (Array.from(hh.options).some((o) => o.value === newHH)) hh.value = newHH;
      if (Array.from(mm.options).some((o) => o.value === newMM)) mm.value = newMM;
      fire();
    },
  };
}

export function buildNumberStepper(
  initial: number,
  min: number,
  max: number,
  step: number,
  suffix = "",
  _opts: Record<string, any> = {},
): NumberStepperControl {
  let val = initial;
  const listeners: Array<(v: number) => void> = [];
  const userListeners: Array<(v: number) => void> = [];
  const display = h("div", { class: "stepper__value" }, val + suffix);
  const update = () => {
    display.textContent = val + suffix;
  };
  const el = h("div", { class: "stepper" }, [
    h(
      "button",
      {
        type: "button",
        class: "stepper__btn",
        "aria-label": "감소",
        onclick: () => {
          val = Math.max(min, val - step);
          update();
          listeners.forEach((fn) => fn(val));
          userListeners.forEach((fn) => fn(val));
        },
      },
      icon("minus", 18),
    ),
    display,
    h(
      "button",
      {
        type: "button",
        class: "stepper__btn",
        "aria-label": "증가",
        onclick: () => {
          val = Math.min(max, val + step);
          update();
          listeners.forEach((fn) => fn(val));
          userListeners.forEach((fn) => fn(val));
        },
      },
      icon("plus", 18),
    ),
  ]);
  return {
    el,
    getValue: () => val,
    setValue: (next: number) => {
      const clamped = Math.max(min, Math.min(max, next));
      if (clamped !== val) {
        val = clamped;
        update();
        listeners.forEach((fn) => fn(val));
      }
    },
    onChange: (fn) => {
      listeners.push(fn);
    },
    onUserChange: (fn) => {
      userListeners.push(fn);
    },
  };
}

// Reusable segment control: [[value, label], ...]
//
// 기존엔 Preact <Segment/>를 mountPreact로 감싸 반환했지만, 이 함수가 다른
// Preact 컴포넌트의 useMemo 안에서 호출되면 nested render가 currentComponent
// 포인터를 깨뜨려 "null is not an object (evaluating 'be.__H')" 크래시 발생.
// imperative h() 기반으로 되돌려 안전.
export function buildSegment<T>(
  options: Array<[T, string]>,
  initial: T,
  onChange: (val: T) => void,
): HTMLElement {
  let active = initial;
  const container = h("div", { class: "segment" }) as HTMLElement;
  options.forEach(([val, label]) => {
    const btn = h("button", {
      type: "button",
      class: "segment__btn" + (val === initial ? " segment__btn--active" : ""),
      onclick: () => {
        if (val === active) return;
        active = val;
        Array.from(container.children).forEach((b, i) => {
          (b as HTMLElement).classList.toggle(
            "segment__btn--active",
            options[i][0] === active,
          );
        });
        onChange(val);
      },
    }, label);
    container.appendChild(btn);
  });
  return container;
}
